import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
// @ts-ignore
import { Tooltip } from '@netbasequid/core'
import { useDisplayTheme, useSysTheme } from 'hooks/useUiState'
import { SystemTheme } from 'hooks/useUiState/types'
import AnnotationIcon from 'components/insightContent/Annotation/AnnotationIcon'
import { getYearDateHourMinuteDisplayNumeric } from 'utils/timeZoneUtil'
import { usePageConfigUser } from 'hooks/usePageConfig'
import { TimelineAnnotation } from 'schemas/TimelineAnnotationSchema'
import { useTranslation } from 'react-i18next'
import { SimplifiedAnnotationsMap } from '../../../types'

const StyledTooltipContent = styled.div<{ $theme: SystemTheme }>`
  color: ${({ $theme }) => $theme.text.primary};
  font-size: ${({ $theme }) => $theme.typescale[12].size};
  line-height: ${({ $theme }) => $theme.typescale[12].lineHeight};

  & > :first-child {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  & > :nth-child(2) {
    margin-top: 4px;
    margin-bottom: 2px;
  }

  & > :last-child {
    color: ${({ $theme }) => $theme.text.placeholder};
  }

  &:has(+ &) {
    padding-bottom: 6px;
  }

  & + & {
    border-top: 1px solid ${({ $theme }) => $theme.common.divider[100]};
    padding-top: 6px;
    padding-bottom: 6px;
  }
`
const ViewMore = styled.div<{ $theme: SystemTheme }>`
  border-top: 1px solid ${({ $theme }) => $theme.common.divider[100]};
  padding-top: 6px;
  text-align: right;
  font-size: ${({ $theme }) => $theme.typescale[12].size};
  line-height: ${({ $theme }) => $theme.typescale[12].lineHeight};
`

export const TooltipContent = ({ annotations }: { annotations: TimelineAnnotation[] }) => {
  const theme = useSysTheme()
  const userConfig = usePageConfigUser()
  const { t } = useTranslation()

  return (
    <div>
      {annotations?.slice(0, 2).map((annotation) => (
        <StyledTooltipContent $theme={theme} key={annotation.id}>
          <div>
            <AnnotationIcon color={annotation.color!} />
            <span>{annotation.owner}</span>
          </div>
          <div>{annotation.description}</div>
          <div>
            {`(${getYearDateHourMinuteDisplayNumeric(
              annotation.startDatetime!,
              userConfig.timeZone
            )} - ${getYearDateHourMinuteDisplayNumeric(
              annotation.endDatetime! - 1,
              userConfig.timeZone
            )})`}
          </div>
        </StyledTooltipContent>
      ))}
      {annotations!.length > 2 && <ViewMore $theme={theme}>{t('clickViewAnnotations')}</ViewMore>}
    </div>
  )
}
const AnnotationTooltipsPortal = ({
  simplifiedAnnotationsMap,
  chartRef
}: {
  simplifiedAnnotationsMap: SimplifiedAnnotationsMap
  chartRef: React.RefObject<{ chart: any; container: React.RefObject<HTMLDivElement> }>
}) => {
  const [annotationEls, setAnnotationEls] = useState<{ key: string; element: Element }[]>([])
  const [redrawCount, setRedrawCount] = useState(0)
  const mode = useDisplayTheme()

  useEffect(() => {
    const chartContainer = chartRef.current?.container.current
    const simplifiedAnnotationsKeysArr = Object.keys(simplifiedAnnotationsMap)
    if (chartContainer) {
      const targetEls = simplifiedAnnotationsKeysArr.reduce(
        (acc: { key: string; element: Element }[], key) => {
          const targetEl = chartContainer?.querySelectorAll(
            `[data-testid=line-x-axis-annotation-${key}]`
          )[0]
          // * targetEl is possible undefine cause when the chart is rerendered using the previous chartContainer
          if (targetEl) return [...acc, { element: targetEl, key }]
          return acc
        },
        []
      )
      if (targetEls.length === simplifiedAnnotationsKeysArr.length) {
        setAnnotationEls(targetEls)
      } else {
        setRedrawCount(redrawCount + 1)
      }
    }
  }, [simplifiedAnnotationsMap, chartRef, setRedrawCount, redrawCount])

  return (
    <>
      {annotationEls.map((annotationEl) =>
        createPortal(
          <Tooltip
            enabled
            content={
              <TooltipContent
                annotations={simplifiedAnnotationsMap?.[annotationEl.key]?.meta?.annotations ?? []}
              />
            }
            mode={mode}
            followCursor={false}>
            <span
              data-testid={`annotation-tooltip-trigger${annotationEl.key}`}
              style={{
                position: 'absolute',
                color: 'transparent',
                cursor: 'pointer'
              }}>
              {simplifiedAnnotationsMap[annotationEl.key]?.count ?? 0}
            </span>
          </Tooltip>,
          annotationEl.element
        )
      )}
    </>
  )
}

export default AnnotationTooltipsPortal
